import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import * as Yup from 'yup';
// @mui
import LoadingButton from '@mui/lab/LoadingButton';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Unstable_Grid2';
// hooks
import { useProfile } from 'src/hooks/use-profile';
// utils
import { fData } from 'src/utils/format-number';
// assets
// components
import FormProvider, {
  RHFTextField,
  RHFUploadAvatar
} from 'src/components/hook-form';
import { useSnackbar } from 'src/components/snackbar';
import { useDeleteFileMutation, useUploadNewFileMutation } from 'src/store/Reducer/file';
import { useUpdateCompanyMutation, } from 'src/store/Reducer/settings';
import { USER_TYPE_CONTRACTOR } from 'src/config-global';

// ----------------------------------------------------------------------

export default function AccountCompany() {

  const { enqueueSnackbar } = useSnackbar();

  const { profile: user } = useProfile();

  const [updateCompany] = useUpdateCompanyMutation();

  const [uploadNewFile] = useUploadNewFileMutation();

  const [deleteFile] = useDeleteFileMutation();


  const UpdateUserSchema = Yup.object().shape({
    company_image: Yup.mixed().nullable(),
    company_name: Yup.mixed().nullable(),
  });

  const defaultValues = useMemo(
    () => ({
      company_image: user?.company?.image || null,
      company_name: user?.company?.name || '',
    }),
    [user?.company?.image, user?.company?.name]
  );

  const methods = useForm({
    resolver: yupResolver(UpdateUserSchema),
    defaultValues,
  });

  const {
    reset,
    setValue,
    handleSubmit,
    formState: { isSubmitting },

  } = methods;

  // Re-seed the form whenever the Get Profile API returns fresh company data.
  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const onSubmit = handleSubmit(async (data) => {
    let imageURL = "";

    try {
      if (data.company_image && typeof data.company_image !== 'string') {
        const pastFileName = user?.company?.image?.split('/').pop();


        if (pastFileName && pastFileName !== 'noimg.png') {
          const deleteFileResponse = await deleteFile({ fileName: pastFileName });

          if (deleteFileResponse?.error) {
            console.error('Failed to delete old file:', deleteFileResponse.error);
            return;
          }
        }

        const uploadedFileResponse = await uploadNewFile({ file: data.company_image });

        if (!uploadedFileResponse?.error) {
          imageURL = uploadedFileResponse?.data?.body?.fileName;
        }
      }

      const currentImageName = user?.company?.image?.split('/').pop();

      const response = await updateCompany({
        role: USER_TYPE_CONTRACTOR,
        data: {
          image: imageURL || currentImageName,
          name: data.company_name,
        },
      });

      if (!response?.error) {
        enqueueSnackbar(response?.data?.message || 'Company updated successfully', { variant: 'success' });
        // 'Profile' tag invalidation refetches the profile → useProfileSync updates the
        // sidebar logo and this form.
      }
    } catch (error) {
      console.error(error);
    }
  });

  const handleDrop = useCallback(
    (acceptedFiles) => {
      const file = acceptedFiles[0];

      const newFile = Object.assign(file, {
        preview: URL.createObjectURL(file),
      });

      if (file) {
        setValue('company_image', newFile, { shouldValidate: true });
      }
    },
    [setValue]
  );


  return (
    <FormProvider methods={methods} onSubmit={onSubmit}>
      <Grid container spacing={3}>
        <Grid xs={12} md={12}>
          <Card sx={{ pt: 10, pb: 5, px: 3, textAlign: 'center' }}>
            <RHFUploadAvatar
              name="company_image"
              maxSize={3145728}
              onDrop={handleDrop}
              helperText={
                <Typography
                  variant="caption"
                  sx={{
                    mt: 3,
                    mx: 'auto',
                    display: 'block',
                    textAlign: 'center',
                    color: 'text.disabled',
                  }}
                >
                  Allowed *.jpeg, *.jpg, *.png, *.gif
                  <br /> max size of {fData(3145728)}
                </Typography>
              }
            />
            <RHFTextField name="company_name" label="Company Name"/>

              <Stack spacing={3} alignItems="flex-end" sx={{ mt: 3 }}>
              <LoadingButton type="submit" variant="contained" loading={isSubmitting}>
                Save Changes
              </LoadingButton>
            </Stack>
          </Card>
        </Grid>
      </Grid>
    </FormProvider>
  );
}
